import React from 'react';
import { AlertTriangle, X, RefreshCw } from 'lucide-react';
import { cn } from '@utils/cn';
import { Button } from './Button';

interface ErrorAlertProps {
  title?: string;
  message: string;
  variant?: 'error' | 'warning' | 'info';
  onRetry?: () => void;
  onDismiss?: () => void;
  retryLabel?: string;
  dismissible?: boolean;
  className?: string;
}

export const ErrorAlert: React.FC<ErrorAlertProps> = ({
  title,
  message,
  variant = 'error',
  onRetry,
  onDismiss,
  retryLabel = 'Try Again',
  dismissible = true,
  className,
}) => {
  const variants = {
    error: {
      container: 'bg-red-50 border-red-200 text-red-800',
      icon: 'text-red-600',
      title: 'text-red-800',
    },
    warning: {
      container: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      icon: 'text-yellow-600',
      title: 'text-yellow-800',
    },
    info: {
      container: 'bg-blue-50 border-blue-200 text-blue-800',
      icon: 'text-blue-600',
      title: 'text-blue-800',
    },
  };

  const variantStyles = variants[variant];

  return (
    <div className={cn('border rounded-lg p-4', variantStyles.container, className)}>
      <div className="flex items-start space-x-3">
        <div className={cn('flex-shrink-0 mt-0.5', variantStyles.icon)}>
          <AlertTriangle className="h-5 w-5" />
        </div>
        
        <div className="flex-1 min-w-0">
          {title && (
            <h3 className={cn('text-sm font-semibold mb-1', variantStyles.title)}>
              {title}
            </h3>
          )}
          <p className="text-sm leading-relaxed">{message}</p>
          
          {onRetry && (
            <div className="mt-3">
              <Button
                size="sm"
                variant="outline"
                onClick={onRetry}
                icon={<RefreshCw className="h-4 w-4" />}
                className="text-xs"
              >
                {retryLabel}
              </Button>
            </div>
          )}
        </div>
        
        {dismissible && onDismiss && (
          <button
            onClick={onDismiss}
            className={cn(
              'flex-shrink-0 p-1 rounded-md hover:bg-black hover:bg-opacity-10 transition-colors',
              variantStyles.icon
            )}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};